import { IAMAPI } from './API'
import memoize from 'memoizee'
import TeamMember from './models/TeamMember'
import {
  GetTeamMembersForWorkspaceMemberParams,
  FindTeamMembersQuery,
  CreateTeamMemberPayload,
  UpdateTeamMemberPayload,
  GetTeamMemberParams,
  GetTeamMembersForUserParams,
  TeamMemberDTO,
  GetPendingMembersForTeamParams,
  PendingTeamMemberDTO,
  GetTeamMembersForWorkspaceByUserPublicId,
} from './dtos/team-member'
import { collection } from 'infra/collection'
import { Observable } from 'rxjs'
import Member from './models/Member'
import { PrimeMember, GetMember } from './MemberStore'
import { PrimeTeam } from './TeamStore'
import { PrimeTeamRole } from './TeamRoleStore'
import { GetCurrentMember } from './SessionStore'
import { Id, PrimeProps, CursorPaged } from 'types/definitions'
import DataLoader from 'dataloader'
import { getCacheKeyForObject } from 'utils/object-util'
import PendingTeamMember from './models/PendingTeamMember'
import { CancellationToken } from 'infra/cancellation'

/**
 * Primes a team member.
 */
export type PrimeTeamMember = (
  props: PrimeProps<TeamMemberDTO, 'team_id' | 'user_id' | 'workspace_id'>
) => TeamMember

/**
 * Callback for fetching a team member.
 */
export type FetchTeamMember = (
  teamId: string,
  userPublicId: string
) => Promise<TeamMember | null>

/**
 * Callback for fetching a users team member objects.
 */
export type FetchUserTeamMembers = (
  params?: GetTeamMembersForWorkspaceMemberParams
) => Promise<Array<TeamMember>>

/**
 * Callback for finding team members by public Id.
 */
export type FindUserTeamMembersByUserPublicId = (
  params: GetTeamMembersForWorkspaceByUserPublicId,
  cancellationToken?: CancellationToken
) => Promise<Array<TeamMember>>

/**
 * Callback for finding team members.
 */
export type FindTeamMembers = (
  params: FindTeamMembersQuery
) => Promise<CursorPaged<TeamMember>>

/**
 * Getter for Team Member.
 */
export type GetTeamMember = (
  teamId: Id,
  userPublicId: Id
) => TeamMember | undefined

/**
 * Getter for Team Members.
 */
export type GetTeamMembers = (teamId: Id) => TeamMember[]

/**
 * Getter for Team Members by public Id.
 */
export type GetTeamMembersByUserPublicId = (
  workspaceId: Id,
  userPublicId: Id
) => TeamMember[]

/**
 * Getter for Team Members by user public Id and team Id.
 */
export type FilterTeamMembers = (publicId: Id, teamId: Id) => TeamMember[]

/**
 * Team Member Store.
 */
export default class TeamMemberStore {
  /**
   * Team Members collection.
   */
  teamMembers = collection<TeamMemberDTO, TeamMember>({
    create: () =>
      new TeamMember(this.primeMember, this.primeTeam, this.primeTeamRole),
    // We don't use the TeamMember `id` for anything, always team + user ID.
    getDataId: (data) => `${data.team_id}/${data.user_id}`,
    getModelId: (model) => `${model.team.id}/${model.member.user.id}`,
  })

  pendingTeamMembers = collection<PendingTeamMemberDTO, PendingTeamMember>({
    create: () => new PendingTeamMember(this.primeTeam, this.primeTeamRole),
    // Use the email here to avoid clashing since it seems invite id's could clash
    getDataId: (data) => `${data.team_id}/${data.email}`,
    getModelId: (model) => `${model.team.id}/${model.email}`,
  })

  teamMemberLoader = new DataLoader<
    GetTeamMemberParams,
    TeamMember | null,
    string
  >(this._fetchMany.bind(this), {
    cacheKeyFn: getCacheKeyForObject,
  })

  constructor(
    private api: IAMAPI,
    private getCurrentMember: GetCurrentMember,
    private getMember: GetMember,
    private primeMember: PrimeMember,
    private primeTeam: PrimeTeam,
    private primeTeamRole: PrimeTeamRole,
    currentMember$: Observable<Member>
  ) {
    this.teamMembersForWorkspaceMember =
      this.teamMembersForWorkspaceMember.bind(this)
    this.getTeamMembers = this.getTeamMembers.bind(this)
    this.getTeamMember = this.getTeamMember.bind(this)
    this.forTeam = this.forTeam.bind(this)
    this.fetchTeamMember = this.fetchTeamMember.bind(this)
    currentMember$.subscribe(async () => {
      // this.teamMembers.clear()
      this.fetchUserTeamMembersForWorkspace.clear()
    })
  }

  /**
   * Returns team members for the specified workspace member, defaulting to the current user.
   *
   * @param opts.workspaceId if specified but user ID isn't, returns the current user's teams in the specified workspace
   * @param opts.userId the user ID
   */
  teamMembersForWorkspaceMember(workspaceId?: Id, userId?: Id) {
    const currentMember = this.getCurrentMember()
    const member =
      this.getMember(workspaceId, userId || currentMember.user.id) ||
      currentMember
    return this.teamMembers.filter((x) => x.member === member)
  }

  /**
   * Gets team members by team ID that are in the collection.
   *
   * @param teamId
   */
  getTeamMembers(teamId: Id) {
    return this.teamMembers.filter((tm) => tm.team.id === teamId)
  }

  /**
   * Gets a local team member by team and user ID.
   * @param {*} teamId
   * @param userPublicId
   */
  getTeamMember(teamId: Id, userPublicId: Id): TeamMember | undefined {
    return this.teamMembers.find(
      (x) => x.team.id === teamId && x.member.user.publicId === userPublicId
    )
  }

  /**
   * Gets local team members by team ID.
   *
   * @param {*} teamId
   */
  forTeam(teamId: string) {
    return this.teamMembers.filter((x) => x.team.id === teamId)
  }

  pendingForTeam(teamId: string) {
    return this.pendingTeamMembers.filter((p) => p.team.id === teamId)
  }

  /**
   * Fetches a team member.
   *
   * @param teamId
   * @param userId
   */
  fetchTeamMember(teamId: string, userId: string): Promise<TeamMember | null> {
    return this.teamMemberLoader.load({ team_id: teamId, user_id: userId })
  }

  /**
   * Fetches team memberships for the specified workspace member. Defaults to current.
   * @param params
   */
  fetchUserTeamMembers = memoize(
    async (
      params: GetTeamMembersForUserParams = {}
    ): Promise<Array<TeamMember>> => {
      const currentMember = this.getCurrentMember()
      return this.api.teamMembers
        .getTeamMembersForUser({
          user_id: Id.assert(params.user_id || currentMember.user.publicId),
          include_team: true,
        })
        .then((data) => {
          return this.teamMembers.set(data)
        })
    },
    {
      promise: true,
      length: 1,
      normalizer: (args) => {
        const params = args[0] || {}
        return params.user_id || ''
      },
    }
  )

  /**
   * Fetches team memberships for the specified workspace member. Defaults to current.
   * @param {*} params
   */
  fetchUserTeamMembersForWorkspace = memoize(
    async (
      params: GetTeamMembersForWorkspaceMemberParams = {}
    ): Promise<Array<TeamMember>> => {
      const currentMember = this.getCurrentMember()
      return this.api.teamMembers
        .getTeamMembersForWorkspaceMember({
          workspace_id: Id.assert(
            params.workspace_id || currentMember.workspace.id
          ),
          user_id: Id.assert(params.user_id || currentMember.user.publicId),
          include_team: true,
        })
        .then((data) => {
          return this.teamMembers.set(data)
        })
    },
    {
      promise: true,
      length: 1,
      normalizer: (args) => {
        const params = args[0] || {}
        return `${params.workspace_id}/${params.user_id}`
      },
    }
  )

  /**
   * Queries for team members.
   *
   * @param {*} params
   */
  find: FindTeamMembers = async (params) => {
    params.limit = 100
    const result = await this.api.teamMembers.find(params)
    const data = this.teamMembers.set(result.data)

    return {
      ...result,
      data,
    }
  }

  /**
   * Queries for team members by public Id.
   */
  search: FindUserTeamMembersByUserPublicId = (
    params: GetTeamMembersForWorkspaceByUserPublicId,
    cancellationToken = CancellationToken.default
  ) =>
    this.api.teamMembers
      .getTeamMembersForWorkspaceMembersByUserPublicId(
        { ...params },
        cancellationToken
      )
      .then((result) => this.teamMembers.set(result))

  async findPendingMembersForTeam(params: GetPendingMembersForTeamParams) {
    const result = await this.api.teamMembers.getPendingMembersForTeam(params)
    return this.pendingTeamMembers.set(result.data)
  }

  /**
   * Adds a workspace member to a team. Requires workspace permissions, not something
   * that a team adminc can do on their own.
   *
   * @param {*} payload
   */
  async addTeamMember(payload: CreateTeamMemberPayload) {
    return this.api.teamMembers
      .create(payload)
      .then((data) => this.teamMembers.set(data))
  }

  /**
   * Updates a team member.
   *
   * @param {*} params
   * @param {*} payload
   */
  async updateTeamMember(
    params: GetTeamMemberParams,
    payload: UpdateTeamMemberPayload
  ) {
    return this.api.teamMembers
      .update(params, payload)
      .then((data) => this.teamMembers.set(data))
  }

  /**
   * Deletes a team member.
   * @param {*} params
   */
  async deleteTeamMember(params: GetTeamMemberParams) {
    await this.api.teamMembers.delete(params)
    const teamMember = this.getTeamMember(params.team_id, params.user_id)
    if (teamMember) this.teamMembers.remove(teamMember)
  }

  /**
   * Fetches many team members. Uses DataLoader.
   * @param keys
   */
  async _fetchMany(
    keys: readonly GetTeamMemberParams[]
  ): Promise<Array<TeamMember | null>> {
    return this.api.teamMembers
      .getMany(keys)
      .then((x) => x.map((v) => this.teamMembers.set(v) || null))
  }

  /**
   * Filters team members by user public Id and Team Id.
   */
  filterTeamMembers(publicId: Id, teamId: Id) {
    return this.teamMembers.filter(
      (m) => m.team.id === teamId && m.member.user.publicId === publicId
    )
  }
}
